import { useCallback, useEffect, useRef, type RefObject } from "react";

type Camera = { x: number; y: number; zoom: number };

// Camera motion never changes game content. Keep high-frequency input outside
// React rendering and coalesce DOM writes to the display's refresh rate.
export function useBoardCamera(viewport: RefObject<HTMLDivElement | null>) {
  const canvas = useRef<HTMLDivElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const camera = useRef<Camera>({ x: 0, y: 0, zoom: 1 });
  const frame = useRef<number | null>(null);
  const paintedZoom = useRef<number | null>(null);
  const setCamera = useCallback(
    (update: Camera | ((current: Camera) => Camera)) => {
      camera.current = typeof update === "function" ? update(camera.current) : update;
      if (frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        const { x, y, zoom } = camera.current;
        if (canvas.current)
          canvas.current.style.transform = `translate(${x}px, ${y}px) scale(${zoom})`;
        if (grid.current) {
          // Repeat one extra cell beyond each edge; panning changes only transform.
          grid.current.style.transform = `translate(${x % (100 * zoom)}px, ${y % (140 * zoom)}px)`;
          if (paintedZoom.current !== zoom) {
            grid.current.style.backgroundSize = `${100 * zoom}px ${140 * zoom}px`;
            viewport.current?.style.setProperty("--board-zoom", String(zoom));
            paintedZoom.current = zoom;
          }
        }
      });
    },
    [viewport],
  );
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    },
    [],
  );
  return { canvas, grid, setCamera };
}
